// Hardcoded Lisa verification briefs for the five canonical demo leads.
// Kept in sync with the same constants in backend/app/routes/auth.py so that
// demo mode (seed-only) and live mode (with the DB) render identical briefs
// on the lead detail page.

export const DEMO_BRIEF_VOLKOV = {
  score: 22,
  tier: 'LIKELY_FAKE',
  verdict_line:
    "I looked into Alexander and his story doesn't hold together — there are multiple red flags that suggest this may not be a legitimate buyer.",
  checks_out: [],
  cant_confirm: [
    "No verifiable employment history — he claims to be a CEO but I can't find any company footprint matching him",
    "His phone number is a New York area code (+1-917) but he says he's relocating from Moscow",
    'Zero digital presence — no public profile, no press mentions, no professional footprint anywhere',
    'No proof of funds or lender pre-approval despite claiming $10M cash budget',
    'Email is on an unverified domain with no public records of him using it elsewhere',
  ],
  worth_knowing: [
    'Fisher Island is one of the most scrutinized luxury markets in the US — buyers there undergo heavy due diligence before any showing',
    "The pattern here — large cash claim, urgent timeline, minimal documentation — matches profiles that typically don't close",
    "If he's legitimate, he should be able to produce a pre-approval letter, bank attestation, or wealth manager reference immediately",
  ],
  my_take:
    "Don't schedule a showing. Ask for proof of funds and source-of-wealth documentation before any further engagement.",
  crm_sync_message:
    "I've already updated your CRM with my findings and flagged him for manual review.",
};

export const DEMO_BRIEF_JAMES_CHEN = {
  score: 92,
  tier: 'LIKELY_REAL',
  verdict_line:
    'I looked into James and his story holds up — everything you were told matches what I could verify.',
  checks_out: [
    'Verified as VP Engineering at Lumen Capital, a publicly traded company with strong financials',
    'Professional profile is consistent: public profile shows 4,800+ connections, verified corporate email',
    'Compensation and equity at his level (public company tech executive) comfortably support a $6-9M budget',
    'Pre-approval letter from JPMorgan confirming up to $8M is on file and valid',
    "Relocation pattern from San Francisco to Miami is consistent with tech executive migration we've seen repeatedly",
  ],
  cant_confirm: [],
  worth_knowing: [
    'He currently owns a San Francisco condo which is listed for sale — liquidity likely lines up with his closing timeline',
    "Lumen Capital's stock performance and his tenure make his financial capacity solid",
    'Tech executive relocations to Miami are typically driven by the no-state-income-tax advantage — expect this to be a permanent move',
  ],
  my_take:
    'Proceed with confidence. Standard practice is to request updated proof of funds at contract stage, but everything here looks clean.',
  crm_sync_message:
    "I've already updated your CRM with his verification details, employment profile, and pre-approval information.",
};

export const DEMO_BRIEF_DAVID_RODRIGUEZ = {
  score: 88,
  tier: 'LIKELY_REAL',
  verdict_line:
    "I looked into David and everything he told you checks out — he's a real buyer with a solid financial profile.",
  checks_out: [
    'Confirmed as Chief Operating Officer at a regional healthcare network in South Florida with 15+ years in executive medicine',
    'Strong digital footprint: verified public profile with hospital system affiliations and medical board listings',
    'His compensation profile easily supports a $3M-5M purchase and lines up with the First Republic pre-approval on file',
    'Long-term Coral Gables resident — property records confirm current homeownership in the area',
    'Pre-approval letter from First Republic is current and the lender is a known tier-1 institution',
  ],
  cant_confirm: [],
  worth_knowing: [
    "He's looking for an upgrade in the same neighborhood, which typically means a strong sense of what he wants and a faster close",
    'Healthcare executives in Miami skew cash-heavy — expect negotiation on terms, not financing contingencies',
    "The Coral Gables / Pinecrest corridor he's targeting has tightened inventory, so he'll move quickly on the right home",
  ],
  my_take:
    'Proceed — this is exactly the kind of qualified local buyer you want. Line up 2-3 strong matches and move fast.',
  crm_sync_message:
    "I've already updated your CRM with his employment profile, verified lender, and Coral Gables property history.",
};

export const DEMO_BRIEF_ROBERTO_SILVA = {
  score: 78,
  tier: 'MOSTLY_REAL',
  verdict_line:
    "I looked into Roberto and most of what he told you lines up — his profile is real, with a few details I couldn't fully confirm.",
  checks_out: [
    'He is listed as an owner of a Brickell-area restaurant group, matching what he told you on the call',
    'Brickell address is consistent with property records in his name going back several years',
    'Has a moderate professional footprint and established Miami business presence',
    'Stated $2M-3M budget is plausible for a successful independent restaurant owner in that corridor',
  ],
  cant_confirm: [
    'The specific revenue scale of his restaurant group — he mentioned multiple locations but I could only verify one under his name',
    'No pre-approval letter on file yet, so his financing path is still a claim, not a confirmation',
    'Whether the cash portion he described is liquid today or tied up in the business',
  ],
  worth_knowing: [
    'Independent hospitality owners in Brickell often buy with a mix of cash and SBA-backed financing — ask early what the split is',
    "He's been a Miami resident long enough that community references should be easy to pull",
    'A 1-3 month timeline plus a Brickell focus suggests he already has a shortlist in mind',
  ],
  my_take:
    'Proceed, but get a pre-approval letter or proof of funds before the first showing — the gap between claim and paper is the only thing to button up.',
  crm_sync_message:
    "I've already updated your CRM with his business profile and the items I flagged as still-to-confirm.",
};

export const DEMO_BRIEF_SARAH_WILLIAMS = {
  score: 52,
  tier: 'CANT_VERIFY',
  verdict_line:
    "I looked into Sarah and honestly I'm working with pretty thin data — there's not enough for me to make a confident call either way.",
  checks_out: [
    'Miami phone number and email address appear consistent with the name she gave you',
    "Nothing in her profile is contradictory or suspicious — she just hasn't left much of a trail for me to verify",
  ],
  cant_confirm: [
    "No clear employment or income signal — I can't tell what she does for a living from what's publicly available",
    'No lender pre-approval on file and no proof of funds shared so far',
    'Minimal digital presence — no professional profile I could find for someone at her stated budget level',
    'Source-of-wealth or savings context is entirely unverified at this stage',
  ],
  worth_knowing: [
    "A thin digital footprint at the $700K-$900K range isn't unusual — it often means she's a first-time buyer or someone who keeps a low profile, not that she's fake",
    'Her nurture status and exploratory timeline give you room to qualify her properly before committing agent time',
    'The simplest unlock here is asking for a pre-approval letter or a quick financial questionnaire',
  ],
  my_take:
    "Don't book a showing yet. Send her a friendly qualification email asking for pre-approval or proof of funds — if she replies with real documentation, move her up the pipeline.",
  crm_sync_message:
    "I've already updated your CRM with what I could piece together and flagged her as needing more info before a showing.",
};

export const DEMO_BRIEF_SOFIA_MARTINEZ = {
  score: 91,
  tier: 'LIKELY_REAL',
  verdict_line:
    "I looked into Sofia and her story holds up cleanly — she's exactly the kind of local cash buyer you want.",
  checks_out: [
    'Confirmed as a practicing cardiologist affiliated with Baptist Health — her medical board and hospital listings are current',
    'Property records show she owns her current Pinecrest home outright, and it went active on the MLS this week',
    'Her public profile has 10+ years of consistent medical practice in South Florida — no gaps, no inconsistencies',
    "A $4.5M-$6.5M cash budget is well within range for a dual-physician South Florida household at her stage",
    'Miami-Dade records indicate long-standing residency and ties to the Coral Gables / Coconut Grove corridor',
  ],
  cant_confirm: [],
  worth_knowing: [
    "She's selling her Pinecrest home first, but with a cash position the 30-day close she asked for is realistic",
    'Local physicians upsizing inside the Gables / Grove corridor tend to know exactly what they want — expect a fast, decisive process',
    'Spanish is her preferred language, so pairing her with a bilingual agent has clearly helped rapport already',
  ],
  my_take:
    'Proceed with confidence — this is a clean, motivated, local cash buyer. Line up 2-3 matched properties and move quickly.',
  crm_sync_message:
    "I've already updated your CRM with her verified employment, property history, and local residency details.",
};

export const DEMO_BRIEF_PATRICIA_DUBOIS = {
  score: 93,
  tier: 'LIKELY_REAL',
  verdict_line:
    "I looked into Patricia and everything she told you checks out — this is a serious international buyer with the capital she claims.",
  checks_out: [
    'Verified as the founder of an established Paris-based luxury goods house with more than two decades of public trading history',
    "Her public profile is extensive — press coverage, industry features, and verified business registrations all line up with the name she gave you",
    'Wealth profile at the founder level easily supports a $12M-$20M all-cash waterfront purchase',
    'International travel and a three-day Miami tour are consistent with how buyers at this tier shop for second homes',
    'No conflicting identities, no sanctioned-entity matches — she clears the standard screens cleanly',
  ],
  cant_confirm: [],
  worth_knowing: [
    "She's a second-home buyer, so expect her to prioritize privacy, dockage, and turn-key move-in over renovation projects",
    'Founders of European luxury houses typically close through a US-based family office — ask who the point of contact is before the tour',
    "Golden Beach, Key Biscayne, and the North Beach waterfront she's targeting all have very thin inventory above $12M — the right match will go quickly",
    'A 90-day timeline for this tier is a soft target; the real driver is finding the one house, not the calendar',
  ],
  my_take:
    'Proceed — roll out the red carpet. Build a tight three-home waterfront tour and have her family office contact looped in before she lands.',
  crm_sync_message:
    "I've already updated your CRM with her verified business profile, source-of-wealth context, and international screening results.",
};

export const DEMO_BRIEF_CARLOS_MENDEZ = {
  score: 87,
  tier: 'LIKELY_REAL',
  verdict_line:
    "I looked into Carlos and his profile lines up across the board — he's a legitimate LatAm cash buyer on a short timeline.",
  checks_out: [
    'Confirmed as CFO of a Mexico City industrial group with a verifiable corporate footprint and public filings',
    "His public profile has been consistent for years — executive tenure, board listings, and industry press all match the name he gave",
    'Compensation and equity at the CFO level of an industrial group at that scale comfortably support a $3.5M-$5.5M cash purchase',
    'Travel pattern — flying in Friday for a Brickell-focused tour — is exactly what LatAm principal buyers do at this price point',
    "No adverse findings on standard international screens; records indicate a clean profile",
  ],
  cant_confirm: [],
  worth_knowing: [
    'LatAm CFO buyers in Brickell typically want rental-friendly buildings and a clear exit story — ask early whether this is lifestyle, investment, or both',
    "A 30-day timeline with a ready-and-pre-con mix suggests he already has a strong read on the market",
    "Pre-con sales galleries tend to require a meaningful deposit on the same day — make sure he's prepared for that before Friday",
    'Spanish-first communication has clearly worked so far — keeping Diego as the point agent is the right call',
  ],
  my_take:
    'Proceed — tight itinerary, bilingual agent, and a ready-to-write offer kit. This is a Friday-close-the-loop trip, not a discovery visit.',
  crm_sync_message:
    "I've already updated your CRM with his verified executive profile, travel itinerary, and international screening results.",
};

export const DEMO_BRIEF_MARIA_LOPEZ = {
  score: 82,
  tier: 'LIKELY_REAL',
  verdict_line:
    "I looked into Maria and she's a real, qualified local buyer — the one open question is the timing of her current home sale.",
  checks_out: [
    'Confirmed as a partner at a regional South Florida law firm with a verifiable public profile and bar registration',
    'Property records show she owns her current Doral home and has held it for several years',
    'Compensation at her level — equity partner in an established regional firm — supports the $1.5M-$2.4M budget she described',
    "Her digital footprint is consistent: long-standing career, local community ties, and no conflicting identities",
  ],
  cant_confirm: [
    "A specific pre-approval letter — she mentioned needing a lender intro, so the financing path is confirmed-in-concept but not on paper yet",
    'How quickly her Doral home will actually move once listed — that will drive her real timing more than the 3-6 month window she gave you',
  ],
  worth_knowing: [
    'Contingent buyers in this price band typically close faster when they have the lender lined up before the sale closes — a warm intro helps both sides',
    "Her Brickell / Downtown focus and 2-bedroom minimum aligns with a lot of current inventory, so she shouldn't feel time pressure on the search side",
    'Spanish is her preferred language, which has clearly paid off in the rapport she already has with Ana',
  ],
  my_take:
    'Proceed — make the lender intro this week and start previewing 2-3 matched units so she can move the moment her Doral home is under contract.',
  crm_sync_message:
    "I've already updated your CRM with her verified professional profile, current property, and the lender intro still-to-do.",
};

export const DEMO_BRIEF_EMILY_WATSON = {
  score: 84,
  tier: 'LIKELY_REAL',
  verdict_line:
    "I looked into Emily and her profile is solid — this is a real New York media executive seriously evaluating a Miami move.",
  checks_out: [
    'Confirmed as a senior media executive at a well-known New York company — her public profile and tenure are fully consistent',
    'Press mentions and industry features back up her stated role and seniority',
    'Compensation at her level of media leadership supports the $2.8M-$4.2M range she described',
    'New York property records indicate current homeownership consistent with a household at her income tier',
    "No conflicting identities and no red flags on standard screens — she's exactly who she says she is",
  ],
  cant_confirm: [
    'Whether Miami will be a full primary move or a dual-coast arrangement — that will change which neighborhoods really fit',
    'Financing pre-approval — she mentioned 75% financing but no lender letter has been shared yet',
  ],
  worth_knowing: [
    'NYC media relos to Miami typically anchor on schools first, then commute logistics second — her school questions are a strong buying signal',
    'Coconut Grove and Coral Gables both check the school box; sending a quick comparison one-pager would likely accelerate her decision',
    "Her 4-6 month timeline is realistic for a school-calendar-driven family move — plan around the academic year, not the budget",
    "Natalia is the right lead agent here — relo-heavy families respond well to her style",
  ],
  my_take:
    'Proceed — she is the real deal. Send the schools write-up this week and tee up a preview tour the next time she flies down.',
  crm_sync_message:
    "I've already updated your CRM with her verified employment, NYC property history, and the open questions on financing and primary-vs-secondary.",
};

export const DEMO_BRIEF_THOMAS_BROWN = {
  score: 77,
  tier: 'MOSTLY_REAL',
  verdict_line:
    "I looked into Thomas and most of his story lines up — the profile is real, timing is the piece he hasn't nailed down yet.",
  checks_out: [
    'Confirmed as a partner at a Boston-based private equity firm with a verifiable public profile and multi-year tenure',
    'Compensation and carry at the PE partner level comfortably support a $3M-$4.5M all-cash second-home purchase',
    'His public profile — firm website, press, industry commentary — is all consistent with the name and title he gave you',
    'Second-home-in-Miami is a common pattern for Boston PE partners, and his target areas track that demand',
  ],
  cant_confirm: [
    "The specific bonus / carry cycle he's waiting on — that's what drives whether he moves in 90 days or 9 months",
    'Whether he wants the Miami property titled personally or through an LLC — second-home PE buyers often do the latter',
    'No proof of funds on file yet, though his financial profile strongly suggests the liquidity is real',
  ],
  worth_knowing: [
    "Second-home buyers at this tier shop slowly until they don't — when the bonus lands, expect a compressed timeline",
    'Key Biscayne and Coconut Grove waterfront inventory at his price is limited, so early previews matter even in nurture mode',
    'Keeping him warm with quarterly market notes is the right play — no heavy pitch until the cycle signals he is ready',
  ],
  my_take:
    'Keep him warm with market updates and a light-touch check-in near the end of bonus season — he is real, just not yet ready.',
  crm_sync_message:
    "I've already updated your CRM with his verified professional profile and flagged the bonus-cycle timing as the gating factor.",
};

export const DEMO_BRIEF_MICHAEL_PARK = {
  score: 72,
  tier: 'MOSTLY_REAL',
  verdict_line:
    "I looked into Michael and he's a real buyer — just one where the numbers and the market aren't quite lined up yet.",
  checks_out: [
    'Confirmed as a senior software engineer at an established SaaS company with a verifiable public profile and multi-year tenure',
    'His career history is consistent — no gaps, no conflicting identities, clean professional footprint',
    'A senior IC comp profile at a public SaaS company is consistent with a $900K-$1.4M range, especially with equity factored in',
    "He's based in New York currently, which matches the remote-work-relocation narrative he described",
  ],
  cant_confirm: [
    'Whether his employer will support a permanent remote arrangement in Florida — that is the foundation of this entire search',
    'Exact liquidity on the down-payment side, since the budget he described is a stretch against current Brickell inventory',
    'No pre-approval letter on file yet; at 80% financing, lender selection will matter',
  ],
  worth_knowing: [
    "His $900K-$1.4M Brickell target puts him at the lower end of current 1-bed / small 2-bed inventory — expect him to compromise on size or building age",
    'Tech relos to Miami are often driven by state-tax math — if his company blesses the move, the financial upside is meaningful',
    "A 6-12 month timeline gives plenty of runway to get pre-approved and watch the market, but a tighter budget in this segment means preparation is the edge",
  ],
  my_take:
    'Keep him in nurture, send a pre-approval nudge and a realistic Brickell inventory snapshot — once he has the lender letter, we can decide whether to activate him.',
  crm_sync_message:
    "I've already updated your CRM with his verified employment profile and flagged pre-approval plus employer relocation approval as the open items.",
};

export const DEMO_BRIEF_ANA_GARCIA = {
  score: 68,
  tier: 'MOSTLY_REAL',
  verdict_line:
    "I looked into Ana and she's a real buyer with a genuine long-horizon interest — the gaps are on financial readiness, not on her identity.",
  checks_out: [
    'Confirmed as a small business owner in South Florida — her business registration and local community footprint both line up',
    'Records indicate consistent Miami-area residency over several years, with no conflicting profiles',
    "Nothing in her digital presence contradicts the story she gave — modest footprint, but consistent and local",
    'Her Aventura / Sunny Isles focus at the $800K-$1.2M range tracks with current condo inventory for her profile',
  ],
  cant_confirm: [
    'No pre-approval letter on file yet — she told you she is not there yet, and the lender intro is still open',
    "The revenue scale of her business — small, owner-operated businesses often don't show up cleanly in public financial data",
    "Whether the 20% down she'd need is liquid today, or still being assembled",
    "How firm the 12+ month timeline really is — window-shopping can compress fast once pre-approval comes in",
  ],
  worth_knowing: [
    'Owner-operator buyers at this price point almost always need a warm lender intro to actually move — that is the unlock here',
    "Sunny Isles pre-construction pricing tends to surprise window-shoppers — a realistic snapshot would be useful before she gets attached to a specific project",
    'Spanish is her preferred language, so bilingual nurture content will keep her engaged without burning agent time',
  ],
  my_take:
    'Keep her in nurture and make a lender intro this month — if she comes back with a pre-approval, reassess her tier.',
  crm_sync_message:
    "I've already updated your CRM with what I could verify and flagged pre-approval and liquidity as the items to watch.",
};

export const DEMO_BRIEF_FRANK_REED = {
  score: 66,
  tier: 'MOSTLY_REAL',
  verdict_line:
    "I looked into Frank and he's a real person — the issue isn't authenticity, it's that his financial profile sits well below your team's minimum.",
  checks_out: [
    'Verified identity — retail work history and local Miami residency are consistent across public records',
    'No conflicting identities or red flags on the standard screens',
    'Email and phone number both trace back to the same person, with a consistent footprint over time',
    'Nothing suspicious about the inquiry itself — he filled out a legitimate ad-sourced form, not a fake or duplicate submission',
  ],
  cant_confirm: [
    'Whether a 95% financing path would actually clear underwriting at his stated income',
    "Any secondary income, co-borrower, or gift funds that might change the picture — he hasn't shared that context",
    'Whether he understands that $200K-$350K is well below your Miami inventory floor',
  ],
  worth_knowing: [
    'His budget and financing mix place him below your team minimum — this is a disqualified-by-fit situation, not a fraud situation',
    "A short, respectful 'not a match right now, here are some referrals' email is the cleanest close for a real person at the wrong price point",
    "If retail income ever shifts or he partners up with a co-borrower, he could reactivate — worth keeping the record clean for that reason",
  ],
  my_take:
    'Disqualify with care — he is legitimate, just not in your range. A polite close-out note and optional referral list is the right move.',
  crm_sync_message:
    "I've already updated your CRM with his verified identity and flagged him as a fit-mismatch rather than a quality issue.",
};

export const DEMO_BRIEF_KEVIN_HUGHES = {
  score: 50,
  tier: 'CANT_VERIFY',
  verdict_line:
    "I looked into Kevin and honestly the data is thin — I can't make a confident call on this one either way.",
  checks_out: [
    'His phone number and email appear consistent with the name he gave, and records indicate a real person in the Fort Lauderdale area',
    'Nothing in his profile is contradictory or suspicious — just quiet',
  ],
  cant_confirm: [
    "No clear employment or income signal from public sources — I can't tell what he does for a living",
    'No pre-approval letter on file, and no response since the intake form was submitted',
    "Whether he is actually targeting Miami or only Fort Lauderdale — his stated neighborhood is outside your team's service area",
    'Whether the budget he entered reflects a real buying position or a casual browser filling out a form',
  ],
  worth_knowing: [
    'Out-of-territory inquiries that go silent after intake are a very common pattern — most never reactivate',
    "Before investing more agent time, a short text asking whether he is open to Miami-proper would clarify whether this is worth pursuing at all",
    "If he does not respond within another touch or two, moving him to dead is the right call — the data doesn't support deeper effort",
  ],
  my_take:
    "Don't book a showing. Send one clear text asking whether he'd consider Miami inventory and a pre-approval check — if he doesn't respond, close him out.",
  crm_sync_message:
    "I've already updated your CRM with what little I could piece together and flagged the out-of-territory and no-response concerns.",
};

export const DEMO_BRIEF_JENNIFER_DAVIS = {
  score: 46,
  tier: 'CANT_VERIFY',
  verdict_line:
    "I looked into Jennifer and I'm working with very little — what I do know is she asked to stop hearing from you, and that matters more than anything else here.",
  checks_out: [
    "Her email and phone are consistent with a real person, and there's no sign of a duplicate or fake submission",
    "Nothing in her footprint is contradictory — she just hasn't left much of a trail, and that's fine",
  ],
  cant_confirm: [
    "Any employment, income, or financial signal — her public profile is minimal",
    'Whether her original budget ever reflected a real buying position, or whether she was casually exploring',
    "Whether anything has changed on her side since she asked to be taken off the list",
  ],
  worth_knowing: [
    'The explicit do-not-contact request is the binding signal — do not reach out again regardless of what the thin data might suggest',
    "Keeping her record flagged 'do-not-contact' protects the team from compliance risk on any future campaign",
    'If she ever re-engages voluntarily, she becomes a fresh inbound and the clock resets',
  ],
  my_take:
    'Leave her alone. Her do-not-contact request is the only thing that matters here — no outreach, no follow-up.',
  crm_sync_message:
    "I've already updated your CRM to ensure the do-not-contact flag is in place and excludes her from any outreach.",
};

export const DEMO_BRIEF_LINDA_BROOKS = {
  score: 44,
  tier: 'CANT_VERIFY',
  verdict_line:
    "I looked into Linda and there's not much for me to work with — she looks like a real person, but the fit issues make deeper verification low-value.",
  checks_out: [
    'Phone and email appear to belong to the same person, with a modest but consistent footprint',
    'Hospitality-industry presence in South Florida is visible, though details are sparse',
    'No contradictory identities or red flags on the standard screens',
  ],
  cant_confirm: [
    "Her actual income or co-borrower situation — 97% financing at her stated budget is a tough underwriting story",
    'Whether she understands the Miami-proper price floor, given the out-of-service-area note on file',
    'Any pre-approval or proof of funds — nothing has been shared',
  ],
  worth_knowing: [
    "She sits at the intersection of below-minimum and out-of-territory — either one alone would disqualify, and both together make deeper work a poor use of agent time",
    "If she ever partners with a co-borrower or moves into your service area, she could reactivate — worth keeping the record intact",
    'A short, respectful close-out with an optional referral is the cleanest path here',
  ],
  my_take:
    "Disqualify gently. Don't book a showing — a polite decline with a referral option respects her and protects your team's bandwidth.",
  crm_sync_message:
    "I've already updated your CRM with the fit-mismatch flags and the thin-data notes.",
};

export const DEMO_BRIEF_RICHARD_SMITH = {
  score: 26,
  tier: 'LIKELY_FAKE',
  verdict_line:
    "I looked into Richard and his profile doesn't hold together — between the silence, the missing details, and the shape of the inquiry, I don't think this is a real buyer.",
  checks_out: [],
  cant_confirm: [
    'No verifiable employment, income, or financial signal anywhere — the name pulls up nothing I can tie to this inquiry',
    "No response after three outreach attempts over weeks, which is not how serious buyers at any budget behave",
    "Zero digital footprint — no public profile, no social presence, no press or records of any kind matching what he gave you",
    "The ad-sourced submission has the hallmarks of a form fill meant to test the funnel, not a real purchase intent",
    "A '$400K-$700K, Miami, condo, unknown timeline, unknown financing' record with no follow-up is the textbook non-buyer profile",
  ],
  worth_knowing: [
    "Ghost inquiries from paid ad traffic are common and rarely worth sustained effort — this fits that pattern exactly",
    "Even if he is real, the total lack of documentation and responsiveness means there's nothing for an agent to work with",
    'Continuing outreach here spends your team minutes with no realistic upside',
  ],
  my_take:
    "Don't call again. Move him to dead — if he ever responds, he becomes a fresh inbound and we can re-verify from scratch.",
  crm_sync_message:
    "I've already updated your CRM with my findings and flagged the record for closeout.",
};
